import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Checkbox,
  Grid,
  IconButton,
  InputAdornment,
  FormControl,
  Input,
  InputLabel,
  Menu,
  MenuItem,
  Button,
  Skeleton,
  Paper,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  useTheme,
  Badge,
  Tooltip,
  CircularProgress
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import MenuIcon from "@mui/icons-material/Menu";
import FilterListIcon from '@mui/icons-material/FilterList';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import axios from "axios";
import { API_URL, popupTimer } from "../../consts";
import { useDispatch } from "react-redux";
import ConfirmationDialog from "../common/ConfirmationDialog";
import ChipList from "../common/ChipList";
import CollectionItemCard from "../Collection/CollectionItemCard";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import TopicFilter from "../common/TopicFilter";
import MobileFilterView from "../common/MobileFilterView";
const CommunitySpaces = ({ organizationMode = false, organizationId = null, sharedMode = false, myCollectionsMode = false, subscribedMode = false, pageTitle = "Explore Community Collections" }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [data, setData] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [searhTags, setSearchTags] = useState([]);
  const [searchItem, setsearchItem] = useState("");
  const [filterBy, setFilterBy] = useState(["You may love.."]);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removedRateId, setRemovedRateId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [topTopics, setTopTopics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  
  // Add view mode state - default to 'feed' on mobile, 'grid' on desktop
  const [viewMode, setViewMode] = useState(isMobile ? 'feed' : 'grid');
  
  // Add pagination states
  const [spacesPage, setSpacesPage] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreSpaces, setHasMoreSpaces] = useState(true);
  const containerRef = useRef(null);
  
  // Add states for filter visibility
  const [showTopicFilter, setShowTopicFilter] = useState(false);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [mobileTopicsData, setMobileTopicsData] = useState([]);
  const [showMobileTopicModal, setShowMobileTopicModal] = useState(false);

  // Update view mode when screen size changes
  useEffect(() => {
    setViewMode(isMobile ? 'feed' : 'grid');
  }, [isMobile]);

  // Header visibility control for all views
  const headerVisibleTimeoutRef = useRef(null);
  const lastScrollY = useRef(0);
  const isScrolling = useRef(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  
  useEffect(() => {
    // Always show initially
    setHeaderVisible(true);
    
    // Handle scroll for both feed and grid views
    const handleScroll = () => {
      let currentScrollY;
      
      if (viewMode === 'feed') {
        // For feed view, use window scroll
        currentScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
      } else {
        // For grid view, use container scroll
        if (!containerRef.current) return;
        currentScrollY = containerRef.current.scrollTop;
      }
      
      // Always show header at the top
      if (currentScrollY < 10) {
        setHeaderVisible(true);
        isScrolling.current = false;
        
        // Clear existing timeout
        if (headerVisibleTimeoutRef.current) {
          clearTimeout(headerVisibleTimeoutRef.current);
          headerVisibleTimeoutRef.current = null;
        }
        lastScrollY.current = currentScrollY;
        return;
      }
      
      // Only hide/show on actual scroll movement (ignore if scroll position hasn't changed)
      if (Math.abs(currentScrollY - lastScrollY.current) < 3) {
        return;
      }
      
      const scrollDirection = currentScrollY > lastScrollY.current ? 'down' : 'up';
      lastScrollY.current = currentScrollY;
      
      // Show header immediately on scroll up
      if (scrollDirection === 'up') {
        isScrolling.current = false;
        setHeaderVisible(true);
        
        // Clear existing timeout
        if (headerVisibleTimeoutRef.current) {
          clearTimeout(headerVisibleTimeoutRef.current);
          headerVisibleTimeoutRef.current = null;
        }
      } else {
        // Scroll down - hide header immediately
        isScrolling.current = true;
        setHeaderVisible(false);
        
        // Clear existing timeout before setting new one
        if (headerVisibleTimeoutRef.current) {
          clearTimeout(headerVisibleTimeoutRef.current);
          headerVisibleTimeoutRef.current = null;
        }
        
        // Show header after half a second of no scrolling
        headerVisibleTimeoutRef.current = setTimeout(() => {
          // Only show if we're still not scrolling
          if (isScrolling.current) {
            setHeaderVisible(true);
            isScrolling.current = false;
          }
          headerVisibleTimeoutRef.current = null;
        }, 500);
      }
    };

    // Use throttling for better performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    if (viewMode === 'feed') {
      // For feed view, listen to window scroll
      window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    } else {
      // For grid view, listen to container scroll
      const container = containerRef.current;
      if (container) {
        container.addEventListener('scroll', throttledHandleScroll, { passive: true });
      }
    }
    
    return () => {
      if (viewMode === 'feed') {
        window.removeEventListener('scroll', throttledHandleScroll);
      } else {
        const container = containerRef.current;
        if (container) {
          container.removeEventListener('scroll', throttledHandleScroll);
        }
      }
      if (headerVisibleTimeoutRef.current) {
        clearTimeout(headerVisibleTimeoutRef.current);
        headerVisibleTimeoutRef.current = null;
      }
      // Reset to visible when component unmounts or view changes
      isScrolling.current = false;
      setHeaderVisible(true);
    };
  }, [viewMode]);

  // Bottom nav visibility control for feed view on mobile only
  const bottomNavVisibleTimeoutRef = useRef(null);
  
  useEffect(() => {
    // Always show initially
    dispatch({ type: "SET_BOTTOM_NAV_VISIBLE", payload: true });
    
    if (!isMobile || viewMode !== 'feed') {
      // Reset to visible when not in feed view on mobile
      if (bottomNavVisibleTimeoutRef.current) {
        clearTimeout(bottomNavVisibleTimeoutRef.current);
        bottomNavVisibleTimeoutRef.current = null;
      }
      return;
    }

    // For feed view on mobile, listen to window scroll for bottom nav
    const handleBottomNavScroll = () => {
      const currentScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
      
      // Always show nav at the top
      if (currentScrollY < 10) {
        dispatch({ type: "SET_BOTTOM_NAV_VISIBLE", payload: true });
        if (bottomNavVisibleTimeoutRef.current) {
          clearTimeout(bottomNavVisibleTimeoutRef.current);
          bottomNavVisibleTimeoutRef.current = null;
        }
        return;
      }
      
      const scrollDirection = currentScrollY > lastScrollY.current ? 'down' : 'up';
      
      // Show bottom nav immediately on scroll up
      if (scrollDirection === 'up') {
        dispatch({ type: "SET_BOTTOM_NAV_VISIBLE", payload: true });
        
        // Clear existing timeout
        if (bottomNavVisibleTimeoutRef.current) {
          clearTimeout(bottomNavVisibleTimeoutRef.current);
          bottomNavVisibleTimeoutRef.current = null;
        }
      } else {
        // Scroll down - hide bottom nav immediately
        dispatch({ type: "SET_BOTTOM_NAV_VISIBLE", payload: false });
        
        // Clear existing timeout before setting new one
        if (bottomNavVisibleTimeoutRef.current) {
          clearTimeout(bottomNavVisibleTimeoutRef.current);
          bottomNavVisibleTimeoutRef.current = null;
        }
        
        // Show after half a second of no scrolling
        bottomNavVisibleTimeoutRef.current = setTimeout(() => {
          dispatch({ type: "SET_BOTTOM_NAV_VISIBLE", payload: true });
          bottomNavVisibleTimeoutRef.current = null;
        }, 500);
      }
    };

    // Use throttling for better performance
    let ticking = false;
    const throttledHandleBottomNavScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleBottomNavScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleBottomNavScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', throttledHandleBottomNavScroll);
      if (bottomNavVisibleTimeoutRef.current) {
        clearTimeout(bottomNavVisibleTimeoutRef.current);
        bottomNavVisibleTimeoutRef.current = null;
      }
      // Reset to visible when component unmounts or view changes
      dispatch({ type: "SET_BOTTOM_NAV_VISIBLE", payload: true });
    };
  }, [isMobile, viewMode, dispatch]);
  
  const token = useSelector((state) => state.token);
  const setTokenInProcess = useSelector((state) => state.setTokenInProcess);

  let navigate = useNavigate();

  const topics = [
    "Climate Change",
    "Technology Innovations",
    "Artificial Intelligence",
    "Health and Wellness",
    "Mental Health",
    "Politics",
    "Economics",
    "Cryptocurrency",
    "Social Media",
    "Cybersecurity",
    "Education",
    "Sustainability",
    "Global Warming",
    "Space Exploration",
    "Renewable Energy",
    "Human Rights",
    "Science and Research",
    "Digital Transformation",
    "Data Privacy",
    "E-commerce",
    "Virtual Reality",
    "Food Security",
    "Travel and Tourism",
    "Entertainment Industry",
    "Workplace Dynamics",
    "Finance and Investment",
    "Cultural Trends",
    "Environmental Conservation",
    "Public Health",
    "Global Trade",
    "Sports",
    "Soccer",
  ];

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem("yourItemKey");
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const deleteRate = () => {
    let url = API_URL + `/rates/${removedRateId}/delete`;
    axios
      .post(url, null)
      .then((res) => {
        getSpacesRequest(searhTags, selectedTopics);
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
        dispatch({
          type: "SET_POPUP",
          payload: { text: "Rate removed successfully", type: "success" },
        });
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          dispatch({ type: "SET_UNAUTHORIZED" });
          return;
        }
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: err?.response?.data?.error || "Failed to remove rate. Please try again.",
            type: "error",
          },
        });
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
      });
  };



  const scrollRow = (direction, topic) => {
    const row = document.getElementById(`row-${topic}`);
    if (row) {
      const scrollAmount = direction === "left" ? -300 : 300;
      row.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const shouldDisplayArrows = (topic) => {
    const row = document.getElementById(`row-${topic}`);
    if (row) {
      return row.scrollWidth > row.clientWidth;
    }
    return false;
  };


  useEffect(() => {
    if (localStorage.getItem("is_extension_login")) {
      // Clear the flag immediately to prevent redirect loop
      localStorage.removeItem("is_extension_login");
      navigate("/extension-login-success");
    }
  }, []);

  const getSpacesRequest = useCallback((tags, topics, page = 0, append = false) => {
    setIsLoading(!append);
    setIsLoadingMore(append);
    if (!append) setLoadError(false);
    let url;
    let paramsBody;
    if (organizationMode && organizationId && token) {
      // Use dedicated organization collections API (requires auth, validates org access)
      url = API_URL + `/spaces/authenticated/organization/collections`;
      paramsBody = {
        organization_id: organizationId,
        start_from: page * 10,
        size: 10
      };
    } else if (sharedMode && token) {
      // Use shared collections API (spaces where user is collaborator)
      url = API_URL + `/spaces`;
      paramsBody = {
        shared: true
      };
    } else if (myCollectionsMode && token) {
      // Use my collections API (user's own spaces)
      url = API_URL + `/spaces`;
      paramsBody = {
        shared: false
      };
    } else if (subscribedMode && token) {
      // Use subscribed spaces API
      url = API_URL + `/spaces/authenticated/subscribed-spaces`;
      paramsBody = {};
    } else {
      let baseUrl = `/spaces/community`;
      if (token) {
        baseUrl = `/spaces/authenticated/community`;
      }
      url = API_URL + baseUrl;
      paramsBody = {
        tags: tags.length > 0 ? tags.join(",") : null,
        topics: topics.length > 0 ? topics.join(",") : null,
        start_from: page * 10,
        size: 10
      };
    }
    
    axios
      .get(url, { params: paramsBody })
      .then((res) => {
        let spaces = res.data.spaces || [];
        // Subscribed API returns owner; map to user_name for display
        if (subscribedMode && spaces.length > 0) {
          spaces = spaces.map(s => ({ ...s, user_name: s.user_name || s.owner }));
        }
        if (append) {
          setData(prev => [...prev, ...spaces]);
        } else {
          setData(spaces);
        }
        // If we got less than 10 items, there are no more (subscribed returns all at once)
        setHasMoreSpaces(subscribedMode ? false : spaces.length === 10);
        setLoadError(false);
        setIsLoadingMore(false);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoadingMore(false);
        setIsLoading(false);
        setHasMoreSpaces(false);
        if (err.response?.status === 401) {
          dispatch({ type: "SET_UNAUTHORIZED" });
          return;
        }
        // Non-401 failure on a fresh load: surface an error state (with retry),
        // distinct from the genuinely-empty state.
        if (!append) setLoadError(true);
      });
  }, [token, dispatch, organizationMode, organizationId, sharedMode, myCollectionsMode, subscribedMode]);

  // Handle scroll event - works for both feed view (window scroll) and grid view (container scroll)
  const handleScroll = useCallback(() => {
    if (isLoadingMore || !hasMoreSpaces) return;

    // For feed view, use window scroll; for grid view, use container scroll
    let scrollTop, scrollHeight, clientHeight;
    
    if (viewMode === 'feed') {
      scrollTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
      scrollHeight = document.documentElement.scrollHeight;
      clientHeight = window.innerHeight || document.documentElement.clientHeight;
    } else {
      if (!containerRef.current) return;
      const container = containerRef.current;
      scrollTop = container.scrollTop;
      scrollHeight = container.scrollHeight;
      clientHeight = container.clientHeight;
    }

    const isNearBottom = scrollHeight - scrollTop <= clientHeight * 1.5;

    if (isNearBottom && hasMoreSpaces && !isLoadingMore) {
      setIsLoadingMore(true);
      const nextPage = spacesPage + 1;
      setSpacesPage(nextPage);
      getSpacesRequest(searhTags, selectedTopics, nextPage, true);
    }
  }, [spacesPage, isLoadingMore, hasMoreSpaces, searhTags, selectedTopics, getSpacesRequest, viewMode]);

  // Add scroll event listener - window for feed view, container for grid view
  useEffect(() => {
    if (viewMode === 'feed') {
      // For feed view, listen to window scroll
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    } else {
      // For grid view, listen to container scroll
      const container = containerRef.current;
      if (container) {
        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
      }
    }
  }, [handleScroll, viewMode]);

  // Reset pagination when filters change
  useEffect(() => {
    setSpacesPage(0);
    setHasMoreSpaces(true);
    setIsLoadingMore(false);
  }, [filterBy, searhTags, selectedTopics]);

  useEffect(() => {
    if (!setTokenInProcess) {
      getSpacesRequest(searhTags, selectedTopics);

    }
  }, [filterBy, searhTags, selectedTopics, setTokenInProcess, getSpacesRequest, organizationMode, organizationId]);

  // Update active filters count
  useEffect(() => {
    const count = selectedTopics.length + searhTags.length;
    setActiveFiltersCount(count);
  }, [selectedTopics, searhTags]);

  // Open mobile modal when filter is shown on mobile
  useEffect(() => {
    if (isMobile && showTopicFilter && mobileTopicsData.length > 0) {
      const timer = setTimeout(() => {
        setShowMobileTopicModal(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setShowMobileTopicModal(false);
    }
  }, [isMobile, showTopicFilter, mobileTopicsData]);

  const handleTopicsReady = (topics) => {
    setMobileTopicsData(topics);
  };

  const handleMobileTopicApply = (selectedNames) => {
    setSelectedTopics(selectedNames);
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  // Error state (load failed) - distinct from the genuinely-empty state
  const loadErrorContent = (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: { xs: 8, sm: 12 },
      px: { xs: 2, sm: 4 },
      textAlign: 'center',
      width: '100%'
    }}>
      <Box sx={{
        width: { xs: 80, sm: 100 },
        height: { xs: 80, sm: 100 },
        borderRadius: '50%',
        background: 'linear-gradient(135deg, rgba(239,68,68,0.1) 0%, rgba(220,38,38,0.1) 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 3
      }}>
        <FolderOutlinedIcon sx={{
          fontSize: { xs: 40, sm: 50 },
          color: 'rgba(239,68,68,0.6)'
        }} />
      </Box>
      <Typography variant="h5" sx={{
        fontWeight: 600,
        color: 'rgba(30,41,59,0.9)',
        mb: 1.5,
        fontSize: { xs: '1.25rem', sm: '1.5rem' }
      }}>
        Couldn't load collections
      </Typography>
      <Typography variant="body1" sx={{
        color: 'rgba(71,85,105,0.7)',
        maxWidth: '500px',
        fontSize: { xs: '0.9rem', sm: '1rem' },
        lineHeight: 1.6,
        mb: 3
      }}>
        Something went wrong while loading collections. Please check your connection and tap retry.
      </Typography>
      <Button
        variant="contained"
        onClick={() => getSpacesRequest(searhTags, selectedTopics)}
        sx={{
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
          borderRadius: '10px',
          textTransform: 'none',
          fontWeight: 600,
          px: 3,
          py: 1,
          boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)',
          }
        }}
      >
        Retry
      </Button>
    </Box>
  );

  // Space Feed Card Component - Modern Instagram style like FeedCardItem
  const SpaceFeedCard = ({ data, searhTags }) => {
    const getColorForSpace = (data) => {
      const colors = [
        '#6366f1', // Indigo
        '#8b5cf6', // Violet
        '#06b6d4', // Cyan
        '#10b981', // Emerald
        '#f59e0b', // Amber
        '#ef4444'  // Red
      ];

      const firstLetter = data['name']?.[0] || 'A';
      const colorIndex = firstLetter.charCodeAt(0) % colors.length;
      return colors[colorIndex];
    };

    const color = getColorForSpace(data);

    return (
      <Card
        sx={{
          border: "1px solid #efefef",
          borderRadius: 0,
          background: '#fff',
          boxShadow: 'none',
          cursor: "pointer",
          mb: 0,
          borderTop: "1px solid #efefef",
          borderBottom: "1px solid #efefef",
          "&:first-of-type": {
            borderTop: "none"
          },
          transition: 'border-color 0.2s ease',
          "&:hover": {
            borderColor: "#efefef",
          }
        }}
        onClick={(e) => {
          // Don't navigate if clicking on profile image or name
          if (e.target.closest('[data-profile-click]') || 
              e.target.closest('.MuiAvatar-root') ||
              (e.target.tagName === 'TYPography' && e.target.closest('[data-profile-click]'))) {
            return;
          }
          navigate('/spaces/' + data.id);
        }}
      >
          {/* Header Section - Modern Instagram style */}
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (data?.user_name) {
                  navigate(`/user/${data.user_name}`);
                }
              }}
              data-profile-click="true"
              sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
            >
              {data.profile_image_url ? (
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '2px solid rgba(99,102,241,0.2)',
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(139,92,246,0.1) 100%)',
                    cursor: 'pointer',
                  }}
                >
                  <img
                    src={data.profile_image_url}
                    alt={data.user_name || "User"}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                    color: '#fff',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    cursor: 'pointer',
                  }}
                >
                  {data?.user_name?.[0]?.toUpperCase() || data?.name?.[0]?.toUpperCase() || "?"}
                </Box>
              )}
              <Box>
                <Typography variant="body2" sx={{ 
                  fontWeight: 600, 
                  fontSize: '15px', 
                  lineHeight: 1.2,
                  color: 'rgba(30,41,59,0.9)',
                  mb: 0.25,
                  cursor: 'pointer',
                }}>
                  {data.display_name || (data.first_name && data.last_name ? `${data.first_name} ${data.last_name}` : data.first_name || data.last_name || data.user_name || "Anonymous User")}
                </Typography>
                <Typography variant="caption" sx={{ 
                  fontSize: '13px',
                  color: 'rgba(71,85,105,0.6)'
                }}>
                  {data.created_at ? new Date(data.created_at).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  }) : 'Recently'}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  // Handle menu click
                }}
                sx={{ 
                  color: 'rgba(71,85,105,0.6)', 
                  p: 1,
                  borderRadius: '8px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: 'rgba(99,102,241,0.1)',
                    color: '#6366f1',
                    transform: 'scale(1.1)',
                  }
                }}
              >
                <MenuIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Title */}
          <Typography variant="h6" sx={{ 
            fontWeight: 700, 
            mb: 1.5, 
            fontSize: '18px',
            lineHeight: 1.3,
            color: 'rgba(30,41,59,0.9)',
            background: 'linear-gradient(135deg, #1a202c 0%, #6366f1 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            {data.name}
          </Typography>

          {/* Description */}
          {data.description && (
            <Typography variant="body2" sx={{ 
              mb: 2, 
              lineHeight: 1.5, 
              fontSize: '15px',
              color: 'rgba(71,85,105,0.8)',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {data.description}
            </Typography>
          )}

          {/* Image - Modern Instagram style */}
          <Box sx={{ 
            mb: 1.5, 
            borderRadius: 0, 
            overflow: 'hidden',
            marginLeft: { xs: -1.5, sm: -2 },
            marginRight: { xs: -1.5, sm: -2 },
            width: { xs: 'calc(100% + 24px)', sm: 'calc(100% + 32px)' },
            backgroundColor: '#000'
          }}>
            {data?.img_url ? (
              <img
                src={data.img_url}
                alt={data.name || "Collection image"}
                style={{
                  width: '100%',
                  maxHeight: '600px',
                  objectFit: 'contain',
                  objectPosition: 'center',
                  display: 'block'
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "200px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                  color: "#fff",
                  fontSize: "48px",
                  fontWeight: "bold",
                }}
              >
                <Typography sx={{ 
                  fontSize: "48px", 
                  fontWeight: "bold", 
                  textShadow: "0 4px 8px rgba(0,0,0,0.3)",
                  opacity: 0.9
                }}>
                  {data?.name?.[0]?.toUpperCase() || "?"}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Tags - Modern style */}
          {data.keywords && data.keywords.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ 
                color: 'rgba(71,85,105,0.6)', 
                fontSize: '11px', 
                fontWeight: 500, 
                mb: 1, 
                display: 'block',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                TOPICS
              </Typography>
              <ChipList 
                list={data.keywords} 
                highlightList={searhTags} 
                AllowChipOverflow={true} 
              />
            </Box>
          )}
        </CardContent>

        {/* Actions Section - Modern style */}
        <CardActions sx={{ 
          px: { xs: 1.5, sm: 2 }, 
          py: 1, 
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              size="medium"
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                navigate('/spaces/' + data.id);
              }}
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: "white",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: '14px',
                px: 3,
                py: 1,
                minHeight: '36px',
                boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
                "&:hover": {
                  background: 'linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(99,102,241,0.4)',
                }
              }}
            >
              🚀 View Collection
            </Button>
            {data.rates_count && (
              <Typography variant="body2" sx={{ 
                fontSize: '13px', 
                fontWeight: 500,
                color: 'rgba(71,85,105,0.7)',
                background: 'rgba(99,102,241,0.1)',
                px: 1.5,
                py: 0.5,
                borderRadius: '8px'
              }}>
                {data.rates_count} items
              </Typography>
            )}
          </Box>
        </CardActions>
      </Card>
    );
  };

  return (
    <>
      <Box sx={{ 
        minHeight: '100vh',
        background: viewMode === 'feed' ? '#fafafa' : 'linear-gradient(135deg, rgba(248,250,252,0.8) 0%, rgba(255,255,255,0.9) 100%)',
        pt: viewMode === 'feed' ? { xs: 0, sm: 0 } : { xs: 1, sm: 4, md: 6 },
        px: viewMode === 'feed' ? 0 : { xs: viewMode === 'feed' ? 0.5 : 1, sm: 3, md: 4 },
        pb: viewMode === 'feed' ? { xs: 8, sm: 4 } : 0
      }}>
      <Box sx={{ maxWidth: viewMode === 'feed' ? '100%' : '1400px', mx: 'auto' }}>
        <Box sx={{ 
          mb: viewMode === 'feed' ? 0 : { xs: 1, sm: 2 },
          position: viewMode === 'feed' ? 'sticky' : 'relative',
          top: 0,
          zIndex: 10,
          background: viewMode === 'feed' ? '#fff' : 'transparent',
          borderBottom: viewMode === 'feed' ? '1px solid #efefef' : 'none',
          pb: viewMode === 'feed' ? { xs: 1, sm: 1.5 } : 0,
          transform: headerVisible ? 'translateY(0)' : 'translateY(-100%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: viewMode === 'feed' ? 0 : { xs: 0.5, sm: 1 },
            px: viewMode === 'feed' ? { xs: 1, sm: 2 } : { xs: 0.5, sm: 1 },
            py: viewMode === 'feed' ? { xs: 1, sm: 1.5 } : { xs: 0.25, sm: 0.5 },
            maxWidth: viewMode === 'feed' ? { xs: '100%', sm: '600px' } : '100%',
            mx: viewMode === 'feed' ? 'auto' : 0
          }}>
            <Typography 
              variant="h6" 
              component="h1" 
              sx={{ 
                fontWeight: viewMode === 'feed' ? 600 : 700,
                fontSize: viewMode === 'feed' ? { xs: '1.1rem', sm: '1.25rem' } : { xs: '1rem', sm: '1.25rem', md: '1.4rem' },
                background: viewMode === 'feed' ? 'none' : 'linear-gradient(135deg, #1a202c 0%, #6366f1 100%)',
                color: viewMode === 'feed' ? '#262626' : 'transparent',
                WebkitBackgroundClip: viewMode === 'feed' ? 'unset' : 'text',
                WebkitTextFillColor: viewMode === 'feed' ? '#262626' : 'transparent',
                backgroundClip: viewMode === 'feed' ? 'unset' : 'text',
                mb: 0
              }}
            >
              {pageTitle}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {/* View Mode Toggle - Hidden on mobile */}
              {!isMobile && (
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewModeChange}
                  size="small"
                  sx={{
                    background: viewMode === 'feed' ? 'transparent' : 'rgba(255,255,255,0.8)',
                    borderRadius: '8px',
                    border: viewMode === 'feed' ? 'none' : '1px solid rgba(226,232,240,0.6)',
                    boxShadow: viewMode === 'feed' ? 'none' : '0 2px 8px rgba(0,0,0,0.04)',
                    '& .MuiToggleButton-root': {
                      border: 'none',
                      borderRadius: '6px',
                      px: 1.5,
                      py: 0.75,
                      color: viewMode === 'feed' ? '#8e8e8e' : 'rgba(71,85,105,0.8)',
                      fontWeight: 500,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: viewMode === 'feed' ? 'rgba(0,0,0,0.05)' : 'rgba(99,102,241,0.1)',
                        color: viewMode === 'feed' ? '#262626' : 'rgba(99,102,241,0.8)',
                      },
                      '&.Mui-selected': {
                        background: viewMode === 'feed' ? 'transparent' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        color: viewMode === 'feed' ? '#262626' : 'white',
                        boxShadow: viewMode === 'feed' ? 'none' : '0 4px 12px rgba(99,102,241,0.3)',
                        '&:hover': {
                          background: viewMode === 'feed' ? 'transparent' : 'linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)',
                          boxShadow: viewMode === 'feed' ? 'none' : '0 6px 16px rgba(99,102,241,0.4)',
                        }
                      }
                    }
                  }}
                >
                  <ToggleButton value="grid" aria-label="grid view">
                    <ViewModuleIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="feed" aria-label="feed view">
                    <ViewListIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
              
              {/* Filter Button */}
              <Tooltip title={activeFiltersCount > 0 ? "Show active filters" : "Filter collections"} arrow>
                <IconButton 
                  onClick={() => setShowTopicFilter(!showTopicFilter)}
                  size="small"
                  sx={{ 
                    background: viewMode === 'feed' ? 'transparent' : (showTopicFilter 
                      ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' 
                      : 'rgba(255,255,255,0.8)'),
                    backdropFilter: viewMode === 'feed' ? 'none' : 'blur(10px)',
                    border: viewMode === 'feed' ? 'none' : '1px solid rgba(226,232,240,0.6)',
                    borderRadius: viewMode === 'feed' ? '50%' : '12px',
                    width: viewMode === 'feed' ? 36 : 48,
                    height: viewMode === 'feed' ? 36 : 48,
                    color: viewMode === 'feed' ? '#262626' : (showTopicFilter ? 'white' : 'rgba(71,85,105,0.8)'),
                    boxShadow: viewMode === 'feed' ? 'none' : (showTopicFilter 
                      ? '0 4px 12px rgba(99,102,241,0.3)' 
                      : '0 2px 8px rgba(0,0,0,0.04)'),
                    '&:hover': {
                      background: viewMode === 'feed' 
                        ? 'rgba(0,0,0,0.05)' 
                        : (showTopicFilter 
                          ? 'linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)'
                          : 'rgba(99,102,241,0.1)'),
                      borderColor: viewMode === 'feed' ? 'none' : 'rgba(99,102,241,0.3)',
                      transform: viewMode === 'feed' ? 'none' : 'translateY(-2px)',
                      boxShadow: viewMode === 'feed' ? 'none' : (showTopicFilter 
                        ? '0 6px 16px rgba(99,102,241,0.4)'
                        : '0 4px 12px rgba(99,102,241,0.15)'),
                      color: viewMode === 'feed' ? '#262626' : (showTopicFilter ? 'white' : 'rgba(99,102,241,0.8)')
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Badge 
                    badgeContent={activeFiltersCount} 
                    sx={{
                      '& .MuiBadge-badge': {
                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        boxShadow: '0 2px 4px rgba(239,68,68,0.3)',
                        fontSize: '11px',
                        fontWeight: 600
                      }
                    }}
                    invisible={activeFiltersCount === 0}
                  >
                    <FilterListIcon sx={{ fontSize: viewMode === 'feed' ? 18 : 20 }} />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {showTopicFilter && (
            <Box sx={{ 
              maxWidth: viewMode === 'feed' ? { xs: '100%', sm: '600px' } : '1200px',
              background: viewMode === 'feed' ? '#fff' : 'rgba(255,255,255,0.7)',
              backdropFilter: viewMode === 'feed' ? 'none' : 'blur(20px)',
              WebkitBackdropFilter: viewMode === 'feed' ? 'none' : 'blur(20px)',
              borderRadius: 0,
              p: { xs: 1.5, sm: viewMode === 'feed' ? 2 : 3 },
              border: viewMode === 'feed' ? '1px solid #efefef' : '1px solid rgba(226,232,240,0.6)',
              borderTop: '1px solid #efefef',
              borderLeft: 'none',
              borderRight: 'none',
              boxShadow: viewMode === 'feed' ? 'none' : '0 4px 16px rgba(0,0,0,0.06)',
              mb: 0,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              display: { xs: 'none', sm: 'block' },
              mx: viewMode === 'feed' ? 'auto' : 0
            }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {[1, 2, 3, 4, 5].map((_, index) => (
                    <Skeleton 
                      key={index} 
                      variant="rectangular" 
                      width={120} 
                      height={32} 
                      sx={{ 
                        borderRadius: 4,
                        background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                      }} 
                    />
                  ))}
                </Box>
              ) : (
                <TopicFilter
                  onSelect={(topics) => {
                    setFilterBy(topics);
                    setSelectedTopics(topics);
                  }}
                  firstTopics={topTopics}
                  showSearch={showSearch}
                  onSearchToggle={setShowSearch}
                  onTopicsReady={handleTopicsReady}
                />
              )}
              
              {showSearch && (
                <Box sx={{ mt: 2 }}>
                  <FormControl 
                    fullWidth 
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(255,255,255,0.8)',
                        borderRadius: '12px',
                        border: '1px solid rgba(226,232,240,0.6)',
                        '&:hover': {
                          borderColor: 'rgba(99,102,241,0.3)',
                        },
                        '&.Mui-focused': {
                          borderColor: '#6366f1',
                          boxShadow: '0 0 0 3px rgba(99,102,241,0.1)',
                        }
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(71,85,105,0.8)',
                        '&.Mui-focused': {
                          color: '#6366f1',
                        }
                      }
                    }}
                  >
                    <InputLabel htmlFor="search-collections">Search Collections</InputLabel>
                    <Input
                      fullWidth
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setSearchTags([...searhTags, searchItem]);
                          setsearchItem("");
                        }
                      }}
                      id="search-collections"
                      value={searchItem}
                      onChange={(e) => setsearchItem(e.target.value)}
                      startAdornment={
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'rgba(71,85,105,0.6)' }} />
                        </InputAdornment>
                      }
                      sx={{
                        px: 2,
                        py: 1.5,
                        fontSize: '16px',
                        '&::before': { display: 'none' },
                        '&::after': { display: 'none' }
                      }}
                    />
                  </FormControl>
                  
                  {searhTags.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <ChipList 
                        list={searhTags} 
                        onDelete={(index) => setSearchTags(searhTags.filter((_, i) => i !== index))} 
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
          {showTopicFilter && isMobile && !isLoading && (
            <TopicFilter
              onSelect={(topics) => {
                setFilterBy(topics);
                setSelectedTopics(topics);
              }}
              firstTopics={topTopics}
              showSearch={showSearch}
              onSearchToggle={setShowSearch}
              onTopicsReady={handleTopicsReady}
            />
          )}
        </Box>
        
        <Box
          ref={containerRef}
          sx={{ 
            mt: viewMode === 'feed' ? 0 : { xs: 1, sm: 3 },
            background: viewMode === 'feed' ? '#fafafa' : 'transparent',
            minHeight: viewMode === 'feed' ? '100vh' : 'auto',
            maxHeight: viewMode === 'feed' ? 'none' : '80vh',
            overflowY: viewMode === 'feed' ? 'auto' : 'auto',
            p: viewMode === 'feed' ? 0 : { xs: 0.5, sm: 1 },
            maxWidth: viewMode === 'feed' ? { xs: '100%', sm: '600px' } : '1400px',
            mx: 'auto',
            width: '100%',
            // Custom scrollbar - minimal for feed view
            '&::-webkit-scrollbar': {
              width: viewMode === 'feed' ? '0px' : '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: viewMode === 'feed' ? 'transparent' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              borderRadius: '10px',
              '&:hover': {
                background: viewMode === 'feed' ? 'transparent' : 'linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)',
              }
            },
          }}
        >
          <Grid
            container
            spacing={viewMode === 'feed' ? 0 : { xs: 1.5, sm: 2 }}
            sx={{ 
              width: '100%',
              margin: 0,
              '& .MuiGrid-item': {
                padding: viewMode === 'feed' ? { xs: '4px 0', sm: '8px 0' } : undefined
              }
            }}
          >
            <Grid item xs={12}>
              <Typography 
                variant="h5" 
                component="h3" 
                sx={{ 
                  mb: { xs: 1.5, sm: 3 },
                  fontWeight: 600,
                  color: 'rgba(30,41,59,0.9)',
                  fontSize: { xs: '0.9rem', sm: '1.25rem' }
                }}
              >
                You may love..
              </Typography>
            </Grid>
            
            {viewMode === 'feed' ? (
              // Feed View
              <Grid item xs={12}>
                <Box sx={{ maxWidth: '100%', mx: 'auto', px: 0 }}>
                  {isLoading ? (
                    [...Array(3)].map((_, index) => (
                      <Card key={`skeleton-${index}`} sx={{ 
                        border: "1px solid #efefef",
                        borderRadius: 0,
                        boxShadow: "none",
                        mb: 0,
                        backgroundColor: '#fff',
                        borderTop: "1px solid #efefef",
                        borderBottom: "1px solid #efefef",
                        "&:first-of-type": {
                          borderTop: "none"
                        }
                      }}>
                        <CardContent sx={{ p: 2, pb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Skeleton variant="circular" width={32} height={32} sx={{ 
                                background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                              }} />
                              <Box>
                                <Skeleton variant="text" width={120} height={16} sx={{ 
                                  mb: 0.5,
                                  background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                                }} />
                                <Skeleton variant="text" width={80} height={12} sx={{ 
                                  background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                                }} />
                              </Box>
                            </Box>
                            <Skeleton variant="circular" width={24} height={24} sx={{ 
                              background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                            }} />
                          </Box>
                          
                          <Skeleton variant="text" height={20} width="70%" sx={{ 
                            mb: 1,
                            background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                          }} />
                          <Skeleton variant="text" height={16} width="90%" sx={{ 
                            mb: 1,
                            background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                          }} />
                          <Skeleton variant="text" height={16} width="60%" sx={{ 
                            mb: 1.5,
                            background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                          }} />
                          
                          <Skeleton variant="rectangular" height={200} sx={{ 
                            borderRadius: 1, 
                            mb: 1.5,
                            background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                          }} />
                          
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Skeleton variant="rectangular" height={24} width={60} sx={{ 
                              borderRadius: 4,
                              background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                            }} />
                            <Skeleton variant="rectangular" height={24} width={80} sx={{ 
                              borderRadius: 4,
                              background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                            }} />
                            <Skeleton variant="rectangular" height={24} width={70} sx={{ 
                              borderRadius: 4,
                              background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                            }} />
                          </Box>
                        </CardContent>
                        
                        <CardActions sx={{ px: 2, py: 1, borderTop: '1px solid #efefef' }}>
                          <Skeleton variant="rectangular" height={28} width={100} sx={{ 
                            borderRadius: '6px',
                            background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                          }} />
                          <Skeleton variant="text" height={12} width={60} sx={{ 
                            background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                          }} />
                        </CardActions>
                      </Card>
                    ))
                  ) : (
                    loadError ? (
                      loadErrorContent
                    ) : data?.length === 0 ? (
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: { xs: 8, sm: 12 },
                        px: { xs: 2, sm: 4 },
                        textAlign: 'center',
                        width: '100%'
                      }}>
                        <Box sx={{
                          width: { xs: 80, sm: 100 },
                          height: { xs: 80, sm: 100 },
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(139,92,246,0.1) 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3
                        }}>
                          <FolderOutlinedIcon sx={{
                            fontSize: { xs: 40, sm: 50 },
                            color: 'rgba(99,102,241,0.6)'
                          }} />
                        </Box>
                        <Typography variant="h5" sx={{
                          fontWeight: 600,
                          color: 'rgba(30,41,59,0.9)',
                          mb: 1.5,
                          fontSize: { xs: '1.25rem', sm: '1.5rem' }
                        }}>
                          {organizationMode ? 'No Organization Collections Yet' : 'No Collections Yet'}
                        </Typography>
                        <Typography variant="body1" sx={{
                          color: 'rgba(71,85,105,0.7)',
                          maxWidth: '500px',
                          fontSize: { xs: '0.9rem', sm: '1rem' },
                          lineHeight: 1.6
                        }}>
                          {organizationMode
                            ? 'There are no collections in your organization yet. Start by creating your first collection!'
                            : 'There are no collections to display yet. Start exploring and create your first collection!'}
                        </Typography>
                      </Box>
                    ) : (
                      data?.map((item, index) => (
                        <Box key={index} sx={{ mb: { xs: 1, sm: 3 } }}>
                          <SpaceFeedCard
                            data={item}
                            searhTags={searhTags}
                          />
                        </Box>
                      ))
                    )
                  )}
                  {isLoadingMore && (
                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="center" p={4}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 2,
                          background: 'rgba(255,255,255,0.8)',
                          backdropFilter: 'blur(10px)',
                          borderRadius: '16px',
                          p: 3,
                          border: '1px solid rgba(226,232,240,0.6)',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
                        }}>
                          <CircularProgress 
                            size={24}
                            sx={{
                              color: '#6366f1',
                              '& .MuiCircularProgress-circle': {
                                strokeLinecap: 'round',
                              }
                            }}
                          />
                          <Typography sx={{ 
                            color: 'rgba(71,85,105,0.8)',
                            fontWeight: 500
                          }}>
                            Loading more collections...
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Box>
              </Grid>
            ) : (
              // Grid View
              <>
                {isLoading ? (
                  [...Array(6)].map((_, index) => (
                    <Grid item xs={12} sm={6} md={4} key={`skeleton-${index}`} sx={{ 
                      display: 'flex',
                      minHeight: '350px'
                    }}>
                      <Paper 
                        sx={{ 
                          height: '330px',
                          p: 2,
                          borderRadius: '16px',
                          background: 'rgba(255,255,255,0.8)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(226,232,240,0.6)',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                          }
                        }}
                      >
                        <Skeleton 
                          variant="rectangular" 
                          height={160} 
                          sx={{ 
                            borderRadius: '12px', 
                            mb: 2,
                            background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                          }} 
                        />
                        <Skeleton 
                          variant="text" 
                          height={28} 
                          width="80%" 
                          sx={{ 
                            mb: 1,
                            borderRadius: '8px',
                            background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                          }} 
                        />
                        <Skeleton 
                          variant="text" 
                          height={20} 
                          width="60%" 
                          sx={{ 
                            mb: 2,
                            borderRadius: '6px',
                            background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                          }} 
                        />
                        <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                          <Skeleton 
                            variant="rectangular" 
                            height={28} 
                            width={80} 
                            sx={{ 
                              borderRadius: '10px',
                              background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                            }} 
                          />
                          <Skeleton 
                            variant="rectangular" 
                            height={28} 
                            width={80} 
                            sx={{ 
                              borderRadius: '10px',
                              background: 'linear-gradient(90deg, rgba(226,232,240,0.3) 25%, rgba(226,232,240,0.5) 50%, rgba(226,232,240,0.3) 75%)'
                            }} 
                          />
                        </Box>
                      </Paper>
                    </Grid>
                  ))
                ) : (
                  loadError ? (
                    <Grid item xs={12}>
                      {loadErrorContent}
                    </Grid>
                  ) : data?.length === 0 ? (
                    <Grid item xs={12}>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: { xs: 8, sm: 12 },
                        px: { xs: 2, sm: 4 },
                        textAlign: 'center'
                      }}>
                        <Box sx={{
                          width: { xs: 80, sm: 100 },
                          height: { xs: 80, sm: 100 },
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(139,92,246,0.1) 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3
                        }}>
                          <FolderOutlinedIcon sx={{ 
                            fontSize: { xs: 40, sm: 50 },
                            color: 'rgba(99,102,241,0.6)'
                          }} />
                        </Box>
                        <Typography variant="h5" sx={{ 
                          fontWeight: 600,
                          color: 'rgba(30,41,59,0.9)',
                          mb: 1.5,
                          fontSize: { xs: '1.25rem', sm: '1.5rem' }
                        }}>
                          {organizationMode ? 'No Organization Collections Yet' : 'No Collections Yet'}
                        </Typography>
                        <Typography variant="body1" sx={{ 
                          color: 'rgba(71,85,105,0.7)',
                          maxWidth: '500px',
                          fontSize: { xs: '0.9rem', sm: '1rem' },
                          lineHeight: 1.6
                        }}>
                          {organizationMode 
                            ? 'There are no collections in your organization yet. Start by creating your first collection!'
                            : 'There are no collections to display yet. Start exploring and create your first collection!'}
                        </Typography>
                      </Box>
                    </Grid>
                  ) : (
                    data?.map((item, index) => (
                      <Grid 
                        item 
                        xs={12} 
                        sm={6} 
                        md={4} 
                        key={index}
                        sx={{ 
                          display: 'flex',
                          minHeight: '350px'
                        }}
                      >
                        <div style={{ 
                          width: '100%',
                          display: 'flex'
                        }}>
                          <CollectionItemCard
                            isBookmark={true}
                            data={item}
                            searhTags={searhTags}
                            applyMargin={true}
                            showRemove={false}
                            enableDragAndDrop={false}
                            onRemove={(id) => {
                              setRemoveDialogOpen(true);
                              setRemovedRateId(id);
                            }}
                            onRateSuccessfully={() => getSpacesRequest(searhTags, selectedTopics)}
                            onNavigate={(path) => navigate(path)}
                          />
                        </div>
                      </Grid>
                    ))
                  )
                )}
                {isLoadingMore && (
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress />
                    </Box>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </Box>
      </Box>
      </Box>
      
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {topics.map((topic) => (
          <MenuItem key={topic}>
            <Checkbox
              checked={selectedTopics.includes(topic)}
              onChange={() =>
                setSelectedTopics((prev) =>
                  prev.includes(topic) ? prev.filter((t) => t !== topic) : [...prev, topic]
                )
              }
            />
            {topic}
          </MenuItem>
        ))}
      </Menu>

      <ConfirmationDialog
        open={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
        onConfirm={() => {
          deleteRate();
          setRemoveDialogOpen(false);
        }}
        title={"Delete rate"}
        cancelButtonString={"Cancel"}
        confirmButtonString={"Delete"}
        message={"Are you sure you want to delete this rate?"}
      />

      {/* Mobile Filter Modal - Outside filter component */}
      {isMobile && (
        <MobileFilterView
          isOpen={showMobileTopicModal}
          onClose={() => {
            setShowMobileTopicModal(false);
            setShowTopicFilter(false);
          }}
          items={mobileTopicsData}
          selectedItems={selectedTopics}
          onApply={handleMobileTopicApply}
          title="Filter by Topics"
          type="topics"
        />
      )}
    </>
  );
};

export default CommunitySpaces;
